import { FC } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Typography } from 'antd';
import { DashboardOutlined, TeamOutlined, UserOutlined, LogoutOutlined, DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useUserStore } from '@/store/useUserStore';
import { PATHS } from '@/router/paths';
import * as authApi from '@/apis/auth';
import styles from './index.module.scss';

const { Sider, Header, Content } = AntLayout;
const { Text } = Typography;

const Layout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshToken, clearAuth } = useUserStore();

  const menuItems = [
    { key: PATHS.dashboard, icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: PATHS.admins, icon: <TeamOutlined />, label: 'Administrators' },
    { key: PATHS.users, icon: <UserOutlined />, label: 'Users' },
    { key: PATHS.help, icon: <QuestionCircleOutlined />, label: 'Help Requests' },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout({ refresh_token: refreshToken });
    } catch {
      // Ignore logout errors
    } finally {
      clearAuth();
      navigate(PATHS.login, { replace: true });
    }
  };

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleLogout,
    },
  ];

  const selectedKey = menuItems.find((item) => location.pathname.startsWith(item.key))?.key || PATHS.dashboard;

  return (
    <AntLayout className={styles.layout}>
      <Sider className={styles.sidebar} width={260}>
        <div className={styles.logoWrapper}>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>P</span>
            <span className={styles.logoText}>The Patch</span>
          </div>
          <span className={styles.logoBadge}>Admin</span>
        </div>
        <Menu mode="inline" selectedKeys={[selectedKey]} items={menuItems} onClick={handleMenuClick} className={styles.menu} />
        <div className={styles.sidebarFooter}>
          <Text className={styles.version}>v1.0.0</Text>
        </div>
      </Sider>
      <AntLayout>
        <Header className={styles.header}>
          <div className={styles.headerLeft}>
            <Text className={styles.pageTitle}>{menuItems.find((item) => item.key === selectedKey)?.label || 'Dashboard'}</Text>
          </div>
          <div className={styles.headerRight}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <div className={styles.userInfo}>
                <Avatar size={32} className={styles.avatar}>
                  {user?.name?.[0] || user?.email?.[0] || 'A'}
                </Avatar>
                <div className={styles.userDetails}>
                  <Text className={styles.userName}>{user?.name || 'Admin'}</Text>
                  <Text className={styles.userEmail}>{user?.email}</Text>
                </div>
                <DownOutlined className={styles.dropdownIcon} />
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className={styles.main}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
